import { z } from 'zod';
import { moneySchema, planSchema } from './common.js';

/**
 * Billing (CLAUDE.md §3 `billing`): monthly prepaid subscription + one unified
 * credit system. The user-facing word is "credits", never "tokens".
 *
 * Cambodia has no practical card autopay, so there is no automatic overage and
 * no silent charge: the plan grant resets each cycle, purchased credits never
 * expire, plan credits are consumed first, and every AI job reserves before it
 * runs and refunds itself if it fails.
 */

/* ------------------------------------------------------------------ */
/* Entitlements                                                        */
/* ------------------------------------------------------------------ */

export const FEATURES = [
  'payment_manual_qr',
  'payment_aba_listener',
  'payment_bakong',
  'payment_payway',
  'templates_premium',
  'layout_overrides',
  'ai_image_edit',
  'ai_facebook_import',
  'ai_voice_transcribe',
  'ai_support_answers',
  'promotions',
  'affiliate',
  'analytics_advanced',
  'automation_broadcast',
  'automation_winback',
  'assistant',
] as const;
export type Feature = (typeof FEATURES)[number];

export interface PlanDefinition {
  id: 'free' | 'starter' | 'pro' | 'business';
  name: string;
  /** Monthly price in USD cents. */
  priceUsdCents: number;
  /** Granted at the start of every cycle. Does not roll over (§3). */
  monthlyCredits: number;
  /**
   * A one-off grant when the shop is created, on top of the first cycle.
   *
   * Only the free plan has one. It exists so a merchant can finish onboarding
   * — import a catalogue, clean up a few photos, hear the bot answer in Khmer
   * — without hitting a wall on day one, and it does NOT expire with the
   * cycle, so a shop that signs up and gets busy still has it a month later.
   */
  welcomeCredits: number;
  /**
   * How many shops this user may open (§3 billing).
   *
   * The subscription belongs to the user, so this is the only limit counted
   * across shops rather than within one. Credits and feature entitlements are
   * shared for the same reason; product and staff caps stay per shop, because
   * three shops are three storefronts and a shared catalogue cap would make
   * the extra ones worthless.
   */
  maxShops: number;
  maxStaff: number;
  maxProducts: number;
  features: readonly Feature[];
}

/**
 * Every AI feature is available on every plan, including free.
 *
 * They are metered by credits rather than gated by tier, which is the whole
 * point of the credit system: a merchant should be able to try the thing that
 * sells the product — their own photo cleaned up, their own Khmer voice note
 * transcribed — before deciding whether to pay. Tiering them would mean the
 * merchant most likely to be won over never sees them.
 *
 * What is NOT metered stays tiered below: a bank integration cannot be sold
 * "three times to try".
 */
const AI_FEATURES = [
  'ai_facebook_import',
  'ai_image_edit',
  'ai_voice_transcribe',
  'ai_support_answers',
] as const;

/** The commerce floor: every shop can take an order and get paid for it. */
const FREE_FEATURES = ['payment_manual_qr', ...AI_FEATURES] as const;

/**
 * Starter adds no features over free — it buys headroom (§3: "core +
 * manual_qr, small credits"). That is deliberate and it is what the caps
 * below are for. A shop outgrows free by having a catalogue, not by wanting
 * a switch flipped.
 */
const STARTER_FEATURES = FREE_FEATURES;

/** §3: Pro is the ABA listener, full templates and layouts, promotions. */
const PRO_FEATURES = [
  ...STARTER_FEATURES,
  'payment_aba_listener',
  'templates_premium',
  'layout_overrides',
  'promotions',
  'automation_broadcast',
  'automation_winback',
] as const;

/** §3: Business is the official gateways, affiliate, and the assistant. */
const BUSINESS_FEATURES = [
  ...PRO_FEATURES,
  'payment_bakong',
  'payment_payway',
  'affiliate',
  'analytics_advanced',
  'assistant',
] as const;

/**
 * `free` is the default a shop is created on, and it is also where a lapsed
 * subscription lands — the storefront stays up and premium locks. We never
 * kill a shop over a late payment (§3 `billing`).
 *
 * Each tier is a superset of the one below it. Upgrading must never take a
 * feature away, or the pricing page is lying.
 */
export const PLANS: Record<PlanDefinition['id'], PlanDefinition> = {
  free: {
    id: 'free',
    name: 'Free',
    priceUsdCents: 0,
    /**
     * Sized from CREDIT_COSTS below, not picked as a round number: enough to
     * keep a small shop's AI habits alive between cycles — say three cleaned-up
     * product photos (30) and thirty AI-answered customer questions (30) —
     * without covering the volume a working shop actually needs.
     */
    monthlyCredits: 60,
    /**
     * The onboarding tour, also priced from CREDIT_COSTS: five AI images (50),
     * one catalogue import of ten products (30), ten minutes of Khmer voice
     * (20) and twenty AI support answers (20). §10 promises "3–5 free real
     * uses"; this is the upper end of that promise, once.
     */
    welcomeCredits: 120,
    maxShops: 1,
    maxStaff: 1,
    /**
     * The real gate. A merchant can build a storefront that looks like a shop
     * and take real orders on it; they cannot run a full catalogue on it.
     */
    maxProducts: 30,
    features: FREE_FEATURES,
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    priceUsdCents: 1500,
    monthlyCredits: 100,
    welcomeCredits: 0,
    maxShops: 1,
    maxStaff: 1,
    maxProducts: 200,
    features: STARTER_FEATURES,
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    priceUsdCents: 2900,
    monthlyCredits: 400,
    welcomeCredits: 0,
    maxShops: 3,
    maxStaff: 3,
    maxProducts: 1000,
    features: PRO_FEATURES,
  },
  business: {
    id: 'business',
    name: 'Business',
    priceUsdCents: 4900,
    monthlyCredits: 1200,
    welcomeCredits: 0,
    maxShops: 10,
    maxStaff: 10,
    maxProducts: 10000,
    features: BUSINESS_FEATURES,
  },
};

/** The plan every new shop starts on (§3). */
export const DEFAULT_PLAN: PlanDefinition['id'] = 'free';

export function planHasFeature(plan: PlanDefinition['id'], feature: Feature): boolean {
  return PLANS[plan].features.includes(feature);
}

/* ------------------------------------------------------------------ */
/* Credit pricing                                                      */
/* ------------------------------------------------------------------ */

/**
 * Credit price per AI action. These are the §3 reference numbers (1 AI image =
 * 10 credits, 1 voice minute = 2); they get re-derived from measured unit cost
 * once we have measured unit cost against real usage, which is why they live
 * in one table instead of at each call site.
 */
export const CREDIT_COSTS = {
  image_edit: 10,
  facebook_import_per_product: 3,
  menu_import_per_item: 3,
  transcribe_per_minute: 2,
  support_answer: 1,
} as const;
export type CreditAction = keyof typeof CREDIT_COSTS;

/** Add-on packs, paid with the same KHQR ladder we sell to merchants (§3). */
export const CREDIT_PACKS = [
  { id: 'pack_200', credits: 200, priceUsdCents: 500 },
  { id: 'pack_600', credits: 600, priceUsdCents: 1200 },
  { id: 'pack_2000', credits: 2000, priceUsdCents: 3500 },
] as const;

export const creditBalanceSchema = z.object({
  plan: z.number().int(),
  purchased: z.number().int(),
  total: z.number().int(),
  monthlyGrant: z.number().int(),
  cycleEndsAt: z.string().datetime().nullable(),
});
export type CreditBalance = z.infer<typeof creditBalanceSchema>;

export const creditLedgerEntrySchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(['plan_grant', 'purchase', 'consumption', 'refund', 'expiry', 'adjustment']),
  bucket: z.enum(['plan', 'purchased']),
  amount: z.number().int(),
  note: z.string().nullable(),
  refType: z.string().nullable(),
  refId: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export const subscriptionSchema = z.object({
  plan: planSchema,
  status: z.enum(['trial', 'active', 'grace', 'expired']),
  periodStart: z.string().datetime(),
  periodEnd: z.string().datetime(),
  price: moneySchema,
  /** Set while an unpaid renewal QR is outstanding. */
  pendingInvoiceId: z.string().uuid().nullable(),
});

export const purchaseCreditsSchema = z.object({
  packId: z.enum(['pack_200', 'pack_600', 'pack_2000']),
});

export const changePlanSchema = z.object({
  plan: z.enum(['starter', 'pro', 'business']),
});

/** Renewal reminder cadence from §3: T-3 days, then a 3–5 day grace period. */
export const RENEWAL_REMINDER_DAYS = 3;
export const RENEWAL_GRACE_DAYS = 5;
/** Balance alerts fire once each per cycle. */
export const CREDIT_ALERT_THRESHOLDS = [0.8, 1.0] as const;

/**
 * How many more people this shop can add.
 *
 * The owner counts, because the owner is a member — which is the whole reason
 * a free shop can invite nobody: one seat, already occupied by the person
 * doing the inviting.
 *
 * Exported so the server's `createStaffInvite` and the admin's Team panel ask
 * the SAME question. They used to answer it separately, which meant the button
 * stayed enabled on a plan that could not honour it and the merchant found out
 * by pressing it.
 *
 * Never negative: a shop that lapses from Business to free keeps its people
 * (§3 never kills a shop over a late payment), and a caller rendering "seats
 * left" must not be handed minus four.
 */
export function staffSeatsLeft(plan: PlanDefinition['id'], activeMembers: number): number {
  /**
   * An unrecognised plan falls back to the default rather than throwing.
   *
   * The types say this cannot happen; it did. The admin's shop payload
   * promised `plan` and never sent it, so this ran as `PLANS[undefined]` inside
   * a render and took the error boundary — the whole dashboard — with it. A
   * caller reading data off the network deserves a wrong number here, not a
   * white screen, and the server is the real gate either way.
   */
  const definition = PLANS[plan] ?? PLANS[DEFAULT_PLAN];
  return Math.max(0, definition.maxStaff - activeMembers);
}
